import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

// ── Color tokens ──────────────────────────────────────────────────────────────

export const lightTheme = {
  bg:          '#FAFAF8',
  card:        '#ffffff',
  card2:       '#F9FAFB',
  hover:       '#F9FAFB',
  hover2:      '#F3F4F6',
  navBg:       '#ffffff',
  navBorder:   '#E5E7EB',
  text1:       '#111827',
  text2:       '#374151',
  text3:       '#6B7280',
  text4:       '#9CA3AF',
  border1:     '#E5E7EB',
  border2:     '#F3F4F6',
  borderInput: '#D1D5DB',
  overlay:     'rgba(0,0,0,0.4)',
  shadow:      '0 1px 4px rgba(0,0,0,0.04)',
  shadowMd:    '0 4px 24px rgba(0,0,0,0.06)',
  shadowLg:    '0 20px 60px rgba(0,0,0,0.18)',
  amberBg:     '#FFFBEB',
  amberBorder: '#FDE68A',
  amberText:   '#92400E',
  riskItem:    '#FFF5F5',
  toastBg:     '#111827',
  toastColor:  '#F9FAFB',
};

export const darkTheme = {
  // Page & surface backgrounds — deep, rich, blue-black layering
  bg:          '#0A0A0F',   // page background — near-black with blue tint
  card:        '#13131F',   // card surfaces — premium dark blue-black
  card2:       '#0F0F1A',   // table background, secondary surfaces
  hover:       '#1A1A2E',   // row hover — perceptible but not harsh
  hover2:      '#1F1F30',   // secondary hover / focus rings
  // Nav — slightly lighter than page for depth
  navBg:       '#0D0D14',   // nav bar — distinct from page bg
  navBorder:   '#2A2A3F',   // nav bottom border
  // Text — strong contrast hierarchy
  text1:       '#F8FAFC',   // primary — near-pure white, maximum contrast
  text2:       '#CBD5E1',   // secondary body text
  text3:       '#94A3B8',   // muted text (customer names, subtitles)
  text4:       '#64748B',   // very muted / placeholders / timestamps
  // Borders — subtle but present
  border1:     '#2A2A3F',   // primary border — cards, inputs, dividers
  border2:     '#1F1F30',   // secondary / row dividers
  borderInput: '#2A2A3F',   // input / button borders
  // Overlays & shadows
  overlay:     'rgba(0,0,0,0.85)',
  shadow:      '0 1px 3px rgba(0,0,0,0.4)',    // card depth per spec
  shadowMd:    '0 4px 24px rgba(0,0,0,0.6)',
  shadowLg:    '0 20px 60px rgba(0,0,0,0.85)',
  // Amber — solid amber badge with white text (bold, visible)
  amberBg:     '#D97706',   // solid amber background for badges/pills
  amberBorder: '#B45309',   // amber border for outlines
  amberText:   '#ffffff',   // white text on solid amber
  // Other semantic
  riskItem:    '#1F0A0A',
  toastBg:     '#13131F',
  toastColor:  '#F8FAFC',
};

// ── Context ───────────────────────────────────────────────────────────────────

const ThemeContext = createContext({
  darkMode: false,
  t: lightTheme,
  toggleDarkMode: () => {},
});

export function ThemeProvider({ children }) {
  const [userId, setUserId] = useState(null);

  // Initialise from localStorage or OS preference — sync before first paint
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ts_dark_mode');
    if (saved !== null) return saved === 'true';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  // Track auth state so we can sync preference from DB
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Apply data-theme attribute for any CSS-level overrides
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Sync FROM database when user logs in
  useEffect(() => {
    if (!userId) return;
    supabase
      .from('users')
      .select('dark_mode')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        if (data && data.dark_mode !== null && data.dark_mode !== undefined) {
          setDarkMode(data.dark_mode);
          localStorage.setItem('ts_dark_mode', String(data.dark_mode));
        }
      });
  }, [userId]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('ts_dark_mode', String(next));
      if (userId) {
        supabase.from('users').update({ dark_mode: next }).eq('id', userId).then(() => {});
      }
      return next;
    });
  }, [userId]);

  const t = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ darkMode, t, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
